import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { PermissionSortFields, SortDirection, GetPermissionsQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/permission';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const usePermissionsTable = ({ queryOptions }: { queryOptions?: GetPermissionsQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetPermissionsQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as PermissionSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withRoles: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: permissionData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetPermissionsQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      permissionData
        ? { totalCount: permissionData.permissions.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [permissionData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type PermissionsBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function PermissionsBaseTable(props: PermissionsBaseTableProps) {
  const tableProps = usePermissionsTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
